import { lookupToken } from '../erc7730/tokenDecimals.js'
import { CHAIN_ID } from './constants.js'
import { proxyRpc } from './rpcProxy.js'

/**
 * Standing ERC20 allowances from the Safe to a tx's target contract.
 *
 * A tx can move nothing itself and still commit funds: placing a limit order
 * records the order, then a keeper fills it in a separate tx by pulling the
 * quote token through an allowance the Safe granted earlier. The simulation
 * of the order tx shows zero asset movements, so the allowance is the only
 * place the real exposure is visible.
 *
 *   order tx (signed here)       keeper fill (not simulated)
 *   Safe -> createOrder()  ...>  target.transferFrom(Safe, 50k USDC)
 *   sim: no movements              bounded by allowance(Safe, target)  <-- shown
 */

export interface Allowance {
  token: string
  symbol: string
  decimals: number
  amount: bigint
}

const NATIVE = '0x0000000000000000000000000000000000000000'
const ALLOWANCE_SELECTOR = '0xdd62ed3e' // allowance(address,address)

/**
 * Known tokens (tokenDecimals.json) passed as ABI-encoded address arguments in
 * `data`, in order of appearance, deduped. A word-scan rather than a decode, so
 * it works for any function without an ABI; a uint that happens to equal a
 * known token address is harmless here (one extra allowance read).
 */
export function findTokenArgs(data: string): string[] {
  if (!data.startsWith('0x') || data.length < 10) return []
  const args = data.slice(10).toLowerCase()
  const found: string[] = []
  for (let i = 0; i + 64 <= args.length; i += 64) {
    const word = args.slice(i, i + 64)
    if (!word.startsWith('0'.repeat(24))) continue
    const addr = `0x${word.slice(24)}`
    if (addr === NATIVE || found.includes(addr) || !lookupToken(addr)) continue
    found.push(addr)
  }
  return found
}

function pad(addr: string): string {
  return addr.toLowerCase().replace(/^0x/, '').padStart(64, '0')
}

/** Reads allowance(owner, spender) for each token. Throws if any read fails,
 *  so a failed read never renders as "no allowance". */
export async function fetchAllowances(
  owner: string,
  spender: string,
  tokens: string[],
): Promise<Allowance[]> {
  return Promise.all(
    tokens.map(async (token) => {
      const result = await proxyRpc(`eip155:${CHAIN_ID}`, 'eth_call', [
        { to: token, data: `${ALLOWANCE_SELECTOR}${pad(owner)}${pad(spender)}` },
        'latest',
      ])
      if (typeof result !== 'string' || !/^0x[0-9a-fA-F]+$/.test(result)) {
        throw new Error(`allowance read on ${token} returned ${String(result)}`)
      }
      const meta = lookupToken(token)!
      return { token, symbol: meta.symbol, decimals: meta.decimals, amount: BigInt(result) }
    }),
  )
}
